import { format, parseISO } from 'date-fns';
import type { Html, Root, Text } from 'mdast';
import type { Plugin } from 'unified';
import { visit } from 'unist-util-visit';

/** Pattern: `['<isoDate>'.toDatabaseDate().formatMoment('<format>')]` */
const DATE_EMBED_PATTERN =
  /\['([^']+)'\.toDatabaseDate\(\)\.formatMoment\('([^']+)'\)\]/g;

/** The output format for all date embed codes, regardless of the requested moment format. */
const DATE_EMBED_OUTPUT_FORMAT = "MMMM d, yyyy '@ 'h:mm a";

function resolveDateEmbed(isoString: string): string {
  try {
    return format(parseISO(isoString), DATE_EMBED_OUTPUT_FORMAT);
  } catch {
    return isoString;
  }
}

export function resolveEmbedCodesInString(text: string): string {
  return text.replace(DATE_EMBED_PATTERN, (_, isoString: string) =>
    resolveDateEmbed(isoString),
  );
}

/** @internal Remark transformer: resolves embed codes in both text and html nodes. */
function transformEmbedCodes(tree: Root): void {
  visit(tree, 'text', (node: Text) => {
    node.value = resolveEmbedCodesInString(node.value);
  });
  visit(tree, 'html', (node: Html) => {
    node.value = resolveEmbedCodesInString(node.value);
  });
}

/**
 * Remark plugin that resolves custom embed code expressions in message text.
 *
 * Supported patterns:
 * - `['<isoDate>'.toDatabaseDate().formatMoment('<format>')]`
 *   Renders as "MMMM d, yyyy @ h:mm a" (e.g. "January 27, 2026 @ 3:33 PM")
 *
 * The plugin visits both `text` nodes (markdown text) and `html` nodes (raw
 * HTML blocks), so embed codes are resolved whether they appear in plain text
 * or within HTML tags.
 *
 * Add new embed code patterns by extending this plugin with additional
 * replacement handlers in `transformEmbedCodes`.
 */
export function remarkEmbedCodes(): ReturnType<Plugin<[], Root>> {
  return transformEmbedCodes;
}
